/**
 * The Unity brand marks.
 *
 * These wrap the supplied artwork rather than redrawing it: a hand-traced
 * approximation is never quite the logo, and the wordmark's letterforms are
 * specific enough that "close" reads as wrong.
 *
 * Three variants, all generated from the same source file:
 *
 * - `unity-lockup.png` — UNITY with the sword as the I, white on transparent,
 *   so it sits on any surface.
 * - `unity-mark.png`   — the sword alone, for tight or square-ish spaces.
 * - `unity-icon.png`   — the sword on the brand's own navy ground, square. The
 *   full lockup squeezed into 28px is unreadable, so anywhere small gets the
 *   blade alone, which still reads at 16px.
 * - `unity-tile.png`   — the original square, wordmark included, for places
 *   with room for it (README, Discord embeds, social cards).
 *
 * The transparent variants are white artwork, so they need a dark surface
 * underneath — which every place the console uses them provides.
 */

import { cn } from '@/lib/cn'

const LOCKUP = '/brand/unity-lockup.png'
const MARK = '/brand/unity-mark.png'
const TILE = '/brand/unity-tile.png'
const ICON = '/brand/unity-icon.png'

/** UNITY with the sword. Sized by height; the width follows. */
export function UnityLogo({
  className,
  title = 'Unity RP',
}: {
  className?: string
  title?: string
}) {
  return (
    <img
      src={LOCKUP}
      alt={title}
      width={858}
      height={521}
      className={cn('h-10 w-auto select-none', className)}
      draggable={false}
    />
  )
}

/** The sword alone. */
export function UnityMark({ className, title }: { className?: string; title?: string }) {
  return (
    <img
      src={MARK}
      alt={title ?? ''}
      aria-hidden={title ? undefined : true}
      width={150}
      height={521}
      className={cn('h-6 w-auto select-none', className)}
      draggable={false}
    />
  )
}

/**
 * The square app icon: the sword on its navy ground.
 *
 * Used in the sidebar header and anywhere else the brand has to fit a small
 * square. A white-on-transparent mark would float unanchored there, and the
 * full wordmark at this size is illegible.
 */
export function UnityBadge({ className }: { className?: string }) {
  return (
    <img
      src={ICON}
      alt=""
      aria-hidden
      width={512}
      height={512}
      className={cn('size-7 shrink-0 rounded-md select-none', className)}
      draggable={false}
    />
  )
}

/** The original square including the wordmark, for places with room for it. */
export function UnityTile({ className }: { className?: string }) {
  return (
    <img
      src={TILE}
      alt="Unity RP"
      width={512}
      height={512}
      className={cn('size-24 rounded-xl select-none', className)}
      draggable={false}
    />
  )
}
